// TriangleTwoSidesAngleDiagram.js
import React from "react";
import { InlineMath } from "react-katex";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normAngle = (angle) => {
  let a = angle;
  while (a <= -Math.PI) a += 2 * Math.PI;
  while (a > Math.PI) a -= 2 * Math.PI;
  return a;
};

const midAngle = (a, b) => normAngle(a + normAngle(b - a) / 2);

export default function TriangleTwoSidesAngleDiagram({
  size = 360,
  angleDeg = 60,
  sideAB = 6,
  sideAC = 4,
}) {
  const COLORS = {
    base: "currentColor",
    accent: "#f59e0b",
  };

  const rad = (angleDeg * Math.PI) / 180;

  // Budujemy trójkąt w prawdziwych proporcjach, a dopiero potem skalujemy do SVG.
  const rawA = { x: 0, y: 0 };
  const rawB = { x: sideAB, y: 0 };
  const rawC = { x: sideAC * Math.cos(rad), y: -sideAC * Math.sin(rad) };

  const minX = Math.min(rawA.x, rawB.x, rawC.x);
  const maxX = Math.max(rawA.x, rawB.x, rawC.x);
  const minY = Math.min(rawA.y, rawB.y, rawC.y);
  const maxY = Math.max(rawA.y, rawB.y, rawC.y);
  const rawWidth = Math.max(maxX - minX, 1);
  const rawHeight = Math.max(maxY - minY, 1);
  const aspect = rawHeight / rawWidth;

  const svgHeight = clamp(size * (0.42 + aspect * 0.55), 190, size);
  const paddingX = 48;
  const paddingY = 42;
  const scale = Math.min(
    (size - paddingX * 2) / rawWidth,
    (svgHeight - paddingY * 2) / rawHeight
  );

  const drawWidth = rawWidth * scale;
  const drawHeight = rawHeight * scale;
  const left = (size - drawWidth) / 2;
  const top = (svgHeight - drawHeight) / 2;

  const mapPoint = (point) => ({
    x: left + (point.x - minX) * scale,
    y: top + (point.y - minY) * scale,
  });

  const A = mapPoint(rawA);
  const B = mapPoint(rawB);
  const C = mapPoint(rawC);
  const centroid = {
    x: (A.x + B.x + C.x) / 3,
    y: (A.y + B.y + C.y) / 3,
  };

  const LatexLabel = ({
    x,
    y,
    math,
    color = COLORS.base,
    width = 48,
    height = 24,
    anchor = "middle",
    fontSize = 14,
  }) => {
    const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
    const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

    return (
      <foreignObject x={boxX} y={y - height / 2} width={width} height={height}>
        <div style={{ color, fontSize, lineHeight: 1.2, textAlign }}>
          <InlineMath math={String(math)} />
        </div>
      </foreignObject>
    );
  };

  const segmentLabel = (P, Q, label, offset = 15) => {
    const mid = { x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 };
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const len = Math.hypot(dx, dy) || 1;
    const n1 = { x: -dy / len, y: dx / len };
    const n2 = { x: dy / len, y: -dx / len };
    const c1 = { x: mid.x + n1.x * offset, y: mid.y + n1.y * offset };
    const c2 = { x: mid.x + n2.x * offset, y: mid.y + n2.y * offset };
    const d1 = Math.hypot(c1.x - centroid.x, c1.y - centroid.y);
    const d2 = Math.hypot(c2.x - centroid.x, c2.y - centroid.y);
    return d1 >= d2 ? { ...c1, label } : { ...c2, label };
  };

  const pointLabel = (P, label, offset = 16) => {
    const vx = P.x - centroid.x;
    const vy = P.y - centroid.y;
    const len = Math.hypot(vx, vy) || 1;
    const x = P.x + (vx / len) * offset;
    const y = P.y + (vy / len) * offset;
    const anchor = vx > 8 ? "start" : vx < -8 ? "end" : "middle";
    return { x, y, label, anchor };
  };

  const abLabel = segmentLabel(A, B, sideAB, 16);
  const acLabel = segmentLabel(A, C, sideAC, 16);
  const ALabel = pointLabel(A, "A");
  const BLabel = pointLabel(B, "B");
  const CLabel = pointLabel(C, "C");

  const dirAB = Math.atan2(B.y - A.y, B.x - A.x);
  const dirAC = Math.atan2(C.y - A.y, C.x - A.x);
  const angleArcR = clamp(Math.min(Math.hypot(B.x - A.x, B.y - A.y), Math.hypot(C.x - A.x, C.y - A.y)) * 0.18, 18, 34);
  const angleArcStart = {
    x: A.x + angleArcR * Math.cos(dirAB),
    y: A.y + angleArcR * Math.sin(dirAB),
  };
  const angleArcEnd = {
    x: A.x + angleArcR * Math.cos(dirAC),
    y: A.y + angleArcR * Math.sin(dirAC),
  };
  const labelBoost = angleDeg < 40 ? 10 : angleDeg > 120 ? -2 : 3;
  const angleLabelR = angleArcR + 18 + labelBoost;
  const angleLabelDir = midAngle(dirAB, dirAC);
  const angleTextPos = {
    x: A.x + angleLabelR * Math.cos(angleLabelDir),
    y: A.y + angleLabelR * Math.sin(angleLabelDir) - 1,
  };

  return (
    <svg width={size} height={svgHeight} viewBox={`0 0 ${size} ${svgHeight}`}>
      {/* trójkąt */}
      <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="2.4" />
      <line x1={A.x} y1={A.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="2.4" />
      <line x1={B.x} y1={B.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="2.4" />

      {/* kąt przy A */}
      <path
        d={`M ${angleArcStart.x} ${angleArcStart.y} A ${angleArcR} ${angleArcR} 0 0 0 ${angleArcEnd.x} ${angleArcEnd.y}`}
        fill="none"
        stroke={COLORS.accent}
        strokeWidth="2.4"
      />
      <LatexLabel
        x={angleTextPos.x}
        y={angleTextPos.y}
        math={`${angleDeg}^{\\circ}`}
        color={COLORS.accent}
        width={54}
        fontSize={15}
      />

      {/* podpisy boków */}
      <LatexLabel x={abLabel.x} y={abLabel.y} math={abLabel.label} color={COLORS.accent} />
      <LatexLabel x={acLabel.x} y={acLabel.y} math={acLabel.label} color={COLORS.accent} />

      {/* wierzchołki */}
      <circle cx={A.x} cy={A.y} r="4" fill={COLORS.base} />
      <circle cx={B.x} cy={B.y} r="4" fill={COLORS.base} />
      <circle cx={C.x} cy={C.y} r="4" fill={COLORS.base} />

      <LatexLabel x={ALabel.x} y={ALabel.y} math={ALabel.label} anchor={ALabel.anchor} />
      <LatexLabel x={BLabel.x} y={BLabel.y} math={BLabel.label} anchor={BLabel.anchor} />
      <LatexLabel x={CLabel.x} y={CLabel.y} math={CLabel.label} anchor={CLabel.anchor} />
    </svg>
  );
}
